'use client';

import { useState } from 'react';
import { useSession } from 'next-auth/react';
import { deleteUser } from '@/actions';
import { AlertDialogComponent } from '@/components/alert-dialog';
import { LoadingButton } from '@/components/loading-button';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { useTranslations } from '@/hooks/useTranslations';
import { Table } from '@tanstack/react-table';

interface BulkDeleteProps<TData> {
  table: Table<TData>;
}

export function BulkDeleteUsers<TData>({ table }: BulkDeleteProps<TData>) {
  const [open, setOpen] = useState(false);
  const { status, wrap } = useSubmitStatus();
  const { data: session } = useSession();
  const t = useTranslations('AdminUsers');

  const selectedRows = table.getSelectedRowModel().rows;
  const ids = selectedRows
    .map((row) => {
      const original = row.original as { id?: string };
      return original.id;
    })
    .filter((id): id is string => typeof id === 'string');

  const currentUserId = session?.user?.id;
  const isAdmin = session?.user?.role === 'ADMIN';

  // Filter out users that can't be deleted
  const deletableIds = ids.filter((id) => {
    // Admin can delete anyone, or user can delete themselves
    return isAdmin || id === currentUserId;
  });

  const handleBulkDelete = () => {
    // Only proceed if there are deletable users
    if (deletableIds.length === 0) {
      return;
    }

    wrap(async () => {
      for (const id of deletableIds) {
        const result = await deleteUser(id);
        if (result?.error) {
          throw new Error(result.error);
        }
      }
    }).then(() => {
      selectedRows.forEach((row) => row.toggleSelected(false));
      setTimeout(() => setOpen(false), 700);
    });
  };

  return (
    <>
      <button
        className="text-red-600 hover:underline font-medium"
        onClick={() => setOpen(true)}
      >
        {t('deleteSelected')}
      </button>

      <AlertDialogComponent
        open={open}
        onOpenChange={setOpen}
        title={t('deleteSelectedUsers')}
        description={
          deletableIds.length < ids.length
            ? `${t('deleteSelectedUsersDesc').replace('{count}', String(deletableIds.length))} (${ids.length - deletableIds.length} user(s) cannot be deleted - you can only delete your own account or need admin access)`
            : t('deleteSelectedUsersDesc').replace(
                '{count}',
                String(ids.length)
              )
        }
        cancelLabel={t('deleteUserAlert.cancelLabel')}
        deleteLabel={
          <LoadingButton
            isFull={false}
            type="button"
            status={status}
            idleText={t('deleteUserAlert.deleteLabel')}
            loadingText="Deleting..."
            successText="Deleted"
            errorText="Failed"
            onClick={handleBulkDelete}
            disabled={deletableIds.length === 0}
          />
        }
      />
    </>
  );
}
